import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Banknote, Loader2 } from "lucide-react";
import {
  useOvertimePayouts,
  useCreateOvertimePayout,
  useDeleteOvertimePayout,
} from "@/hooks/useEmployeeOvertime";
import { formatDate } from "@/lib/date-utils";

interface OvertimePayoutFormProps {
  personId: string;
  year: number;
}

export function OvertimePayoutForm({
  personId,
  year,
}: OvertimePayoutFormProps) {
  const { data: payouts = [], isLoading } = useOvertimePayouts(personId, year);
  const createPayout = useCreateOvertimePayout();
  const deletePayout = useDeleteOvertimePayout();

  const [hours, setHours] = useState("");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");

  const handleAdd = () => {
    const h = parseFloat(hours);
    if (!h || !date) return;
    createPayout.mutate({
      person_id: personId,
      hours: h,
      date,
      notes: notes || null,
    });
    setHours("");
    setDate("");
    setNotes("");
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Banknote className="h-4 w-4" />
          Overtime Payouts {year}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="w-20">
            <Label className="text-xs">Hours</Label>
            <Input
              type="number"
              step="0.5"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="8"
            />
          </div>
          <div>
            <Label className="text-xs">Date</Label>
            <DatePicker value={date} onChange={(v) => setDate(v)} />
          </div>
          <div className="flex-1">
            <Label className="text-xs">Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
            />
          </div>
          <Button
            size="icon"
            onClick={handleAdd}
            disabled={createPayout.isPending || !hours || !date}
          >
            {createPayout.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : payouts.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-2">
            No payouts recorded
          </p>
        ) : (
          <div className="space-y-1">
            {payouts.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50"
              >
                <div className="text-sm">
                  <span className="font-medium font-mono">
                    {Number(p.hours).toFixed(1)}h
                  </span>
                  <span className="text-muted-foreground ml-2">
                    {formatDate(p.date)}
                  </span>
                  {p.notes && (
                    <span className="text-xs text-muted-foreground ml-2">
                      {p.notes}
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => deletePayout.mutate(p.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
